/**
 * Image optimization helper (Tasks #180 + #181).
 *
 * Two strategies in one helper:
 *
 * 1. For absolute https:// URLs on an allowlisted host, returns a
 *    `/img?u=…&w=…` URL served as a resized WebP by the on-the-fly sharp
 *    proxy in `server/routes.ts`.
 * 2. For relative `/objects/...` and `/api/public/product-images/...` URLs
 *    (Task #181), returns the sibling pre-generated WebP variant URL
 *    (e.g. `/objects/pallets/foo.400.webp`) that was created at upload time.
 *
 * For URLs we can't safely proxy/rewrite (data:, blob:, asset bundle paths,
 * already-WebP variants, etc.) we fall back to the original URL unchanged.
 */

const VARIANT_WIDTHS = [400, 800, 1600] as const;

function pickNearestVariantWidth(target: number): number {
  // Snap an arbitrary target width up to the nearest pre-generated variant.
  for (const w of VARIANT_WIDTHS) {
    if (target <= w) return w;
  }
  return VARIANT_WIDTHS[VARIANT_WIDTHS.length - 1];
}

const RELATIVE_VARIANT_PREFIXES = [
  "/objects/",
  "/api/public/product-images/",
  "/api/public-assets/category-showroom/",
  "/api/public-assets/category-hero-images/",
  "/api/public-assets/category-icons/",
  "/api/public-assets/product-images/",
  "/api/public-assets/product-type-images/",
];

function isRewritableRelativePath(src: string): boolean {
  if (!src.startsWith("/")) return false;
  // Already a generated variant — leave it alone.
  if (/\.(400|800|1600)\.webp$/i.test(src)) return false;
  // Only resize jpeg/png/webp originals.
  if (!/\.(jpe?g|png|webp)(\?|#|$)/i.test(src)) return false;
  return RELATIVE_VARIANT_PREFIXES.some((p) => src.startsWith(p));
}

function siblingVariantUrl(src: string, width: number): string {
  // Split off any querystring/hash so the variant URL stays clean.
  const m = src.match(/^([^?#]+)([?#].*)?$/);
  const pathOnly = m ? m[1] : src;
  const rest = m && m[2] ? m[2] : "";
  const base = pathOnly.replace(/\.(jpe?g|png|webp)$/i, "");
  return `${base}.${width}.webp${rest}`;
}

const PROXY_HOST_ALLOWLIST = [
  "storage.googleapis.com",
  "lh3.googleusercontent.com",
  "openbox.shopjimmy.com",
];

function isProxyableHost(host: string): boolean {
  if (PROXY_HOST_ALLOWLIST.includes(host)) return true;
  if (host.endsWith(".replit.dev")) return true;
  if (host.endsWith(".replit.app")) return true;
  if (host.endsWith(".googleusercontent.com")) return true;
  return false;
}

export interface OptimizedImageOpts {
  /** Target rendered width in CSS pixels. */
  width: number;
  /** WebP quality (40-95, default 78). */
  quality?: number;
}

export function optimizeImageUrl(
  src: string | null | undefined,
  opts: OptimizedImageOpts,
): string | undefined {
  if (!src) return undefined;
  // Account for hi-DPI displays so the resized image still looks crisp.
  const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
  const targetW = Math.max(1, Math.round(opts.width * dpr));
  const q = opts.quality ?? 78;

  // Pre-generated WebP variants for relative object-storage paths (Task #181).
  if (isRewritableRelativePath(src)) {
    const w = pickNearestVariantWidth(targetW);
    return siblingVariantUrl(src, w);
  }

  // Skip non-http URLs (data:, blob:, asset bundle paths handled by Vite, …).
  if (!/^https?:\/\//i.test(src)) return src;
  let u: URL;
  try {
    u = new URL(src);
  } catch {
    return src;
  }
  if (!isProxyableHost(u.hostname)) return src;
  return `/img?u=${encodeURIComponent(src)}&w=${targetW}&q=${q}&f=webp`;
}

/**
 * Build a `srcset` string for `<img srcset>` so browsers can pick the
 * smallest sufficient variant. Falls back to undefined when the source
 * can't be proxied.
 */
export function optimizedSrcSet(
  src: string | null | undefined,
  widths: number[],
): string | undefined {
  if (!src) return undefined;

  // Pre-generated WebP variants for relative object-storage paths (Task #181).
  if (isRewritableRelativePath(src)) {
    const seen = new Set<number>();
    const entries: string[] = [];
    for (const w of widths) {
      const snapped = pickNearestVariantWidth(w);
      if (seen.has(snapped)) continue;
      seen.add(snapped);
      entries.push(`${siblingVariantUrl(src, snapped)} ${snapped}w`);
    }
    return entries.length > 0 ? entries.join(", ") : undefined;
  }

  if (!/^https?:\/\//i.test(src)) return undefined;
  let u: URL;
  try {
    u = new URL(src);
  } catch {
    return undefined;
  }
  if (!isProxyableHost(u.hostname)) return undefined;
  return widths
    .map((w) => `/img?u=${encodeURIComponent(src)}&w=${w}&q=78&f=webp ${w}w`)
    .join(", ");
}
